import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Returns map of projectId -> canEdit (admin OR owner OR PUP can_edit=true).
 * Mirrors the gate used by the Edit dialog popup so inline-edit cells stay consistent.
 * RLS remains source of truth on save.
 */
export const useProjectsEditPermissions = (projectIds: string[]) => {
  const { user } = useAuth();
  const key = [...projectIds].sort().join(',');

  return useQuery({
    queryKey: ['projects-edit-permissions', user?.id, key],
    queryFn: async (): Promise<Record<string, boolean>> => {
      if (!user?.id || projectIds.length === 0) return {};

      // 1. Admin bypass
      const { data: isAdmin } = await supabase.rpc('is_admin', { user_id: user.id });
      if (isAdmin) {
        return Object.fromEntries(projectIds.map((id) => [id, true]));
      }

      // 2. PUP can_edit + owner check in parallel
      const [pupResult, ownedResult] = await Promise.all([
        supabase
          .from('project_user_permissions')
          .select('project_id, can_edit')
          .eq('user_id', user.id)
          .in('project_id', projectIds),
        supabase
          .from('projects')
          .select('id')
          .eq('user_id', user.id)
          .in('id', projectIds),
      ]);

      const map: Record<string, boolean> = Object.fromEntries(
        projectIds.map((id) => [id, false])
      );
      (pupResult.data ?? []).forEach((r: any) => {
        if (r.can_edit) map[r.project_id] = true;
      });
      (ownedResult.data ?? []).forEach((p: any) => {
        map[p.id] = true;
      });

      return map;
    },
    enabled: !!user?.id && projectIds.length > 0,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
};
